/**
 * 用戶設定同步服務
 * 
 * 功能：
 * - 登入用戶：設定同步到 Firestore
 * - 游客用戶：設定保存在 localStorage
 * - 自動合併本地和雲端設定
 * 
 * @author AI Assistant
 * @date 2025-11-02
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { TimecodePosition, ScreenshotSize, StepUnit } from '../types';

// 用戶設定接口
export interface UserSettings {
  // 時間碼設定
  timecodePosition: TimecodePosition;
  timecodeScale: number;
  showTimecode: boolean;
  showTimecodeBackground: boolean;
  
  // 播放設定
  fastForwardSeconds: number;
  stepUnit: StepUnit;
  playbackDuration: number;
  playbackRate: number;
  
  // 其他設定
  screenshotSize: ScreenshotSize;
  showWatermarkInOutput: boolean;
  isSoundEnabled: boolean;
  enableFadeEffect: boolean;
  subtitleFillLines: number;
  
  // UI 設定
  showSafetyFrame: boolean;
  gridMode: string;
  showMovieMask: boolean;
  showClock: boolean;
  showCurrentTime: boolean;
  showRemainingTime: boolean;
  timeFormat: string;
  
  // 最後更新時間
  updatedAt?: number;
}

// 預設設定
export const DEFAULT_USER_SETTINGS: UserSettings = {
  timecodePosition: 'bottomRight',
  timecodeScale: 1.0,
  showTimecode: true,
  showTimecodeBackground: true,
  fastForwardSeconds: 5,
  stepUnit: 'second',
  playbackDuration: 30,
  playbackRate: 1,
  screenshotSize: '1:1',
  showWatermarkInOutput: true,
  isSoundEnabled: false,
  enableFadeEffect: true,
  subtitleFillLines: 5,
  showSafetyFrame: false,
  gridMode: 'none',
  showMovieMask: false,
  showClock: false,
  showCurrentTime: false,
  showRemainingTime: false,
  timeFormat: 'timecode',
};

/**
 * 從 localStorage 載入設定
 */
export const loadSettingsFromLocalStorage = (): Partial<UserSettings> => {
  const settings: Partial<UserSettings> = {};
  
  try {
    const timecodePosition = localStorage.getItem('timecodePosition');
    if (timecodePosition) settings.timecodePosition = timecodePosition as TimecodePosition;
    
    const timecodeScale = localStorage.getItem('timecodeScale');
    if (timecodeScale) settings.timecodeScale = parseFloat(timecodeScale);
    
    const showTimecode = localStorage.getItem('showTimecode');
    if (showTimecode !== null) settings.showTimecode = showTimecode !== 'false';
    
    const showTimecodeBackground = localStorage.getItem('showTimecodeBackground');
    if (showTimecodeBackground !== null) settings.showTimecodeBackground = showTimecodeBackground !== 'false';
    
    const fastForwardSeconds = localStorage.getItem('fastForwardSeconds');
    if (fastForwardSeconds) settings.fastForwardSeconds = parseInt(fastForwardSeconds);
    
    const stepUnit = localStorage.getItem('stepUnit');
    if (stepUnit) settings.stepUnit = stepUnit as StepUnit;
    
    const playbackDuration = localStorage.getItem('playbackDuration');
    if (playbackDuration) settings.playbackDuration = parseInt(playbackDuration);
    
    const screenshotSize = localStorage.getItem('screenshotSize');
    if (screenshotSize) settings.screenshotSize = screenshotSize as ScreenshotSize;
    
    const showWatermarkInOutput = localStorage.getItem('showWatermarkInOutput');
    if (showWatermarkInOutput !== null) settings.showWatermarkInOutput = showWatermarkInOutput !== 'false';
    
    const isSoundEnabled = localStorage.getItem('isSoundEnabled');
    if (isSoundEnabled !== null) settings.isSoundEnabled = isSoundEnabled === 'true';
    
    const enableFadeEffect = localStorage.getItem('enableFadeEffect');
    if (enableFadeEffect !== null) settings.enableFadeEffect = enableFadeEffect !== 'false';
    
    const subtitleFillLines = localStorage.getItem('subtitleFillLines');
    if (subtitleFillLines) settings.subtitleFillLines = parseInt(subtitleFillLines);
    
    console.log('📂 從 localStorage 載入設定:', settings);
  } catch (error) {
    console.error('❌ 從 localStorage 載入設定失敗:', error);
  }
  
  return settings;
};

/**
 * 保存設定到 localStorage
 */
export const saveSettingsToLocalStorage = (settings: Partial<UserSettings>): void => {
  try {
    Object.entries(settings).forEach(([key, value]) => {
      if (key !== 'updatedAt') {
        localStorage.setItem(key, String(value));
      }
    });
    console.log('💾 已保存設定到 localStorage:', settings);
  } catch (error) {
    console.error('❌ 保存設定到 localStorage 失敗:', error);
  }
};

/**
 * 從 Firestore 載入用戶設定
 */
export const loadSettingsFromFirestore = async (userId: string): Promise<Partial<UserSettings>> => {
  try {
    const settingsDoc = await getDoc(doc(db, 'userSettings', userId));
    
    if (settingsDoc.exists()) {
      const data = settingsDoc.data() as UserSettings;
      console.log('☁️ 從 Firestore 載入設定:', data);
      return data;
    } else {
      // 调试日志已移除
      return {};
    }
  } catch (error) {
    console.error('❌ 從 Firestore 載入設定失敗:', error);
    return {};
  }
};

/**
 * 保存設定到 Firestore
 */
export const saveSettingsToFirestore = async (userId: string, settings: Partial<UserSettings>): Promise<void> => {
  try {
    const settingsWithTimestamp = {
      ...settings,
      updatedAt: Date.now()
    };
    
    const settingsRef = doc(db, 'userSettings', userId);
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      await updateDoc(settingsRef, settingsWithTimestamp);
      console.log('☁️ 已更新 Firestore 設定:', settingsWithTimestamp);
    } else {
      await setDoc(settingsRef, settingsWithTimestamp);
      console.log('☁️ 已創建 Firestore 設定:', settingsWithTimestamp);
    }
  } catch (error) {
    console.error('❌ 保存設定到 Firestore 失敗:', error);
    throw error;
  }
};

/**
 * 合併本地和雲端設定（雲端優先）
 */
export const mergeSettings = (
  localSettings: Partial<UserSettings>,
  cloudSettings: Partial<UserSettings>
): UserSettings => {
  return {
    ...DEFAULT_USER_SETTINGS,
    ...localSettings,
    ...cloudSettings, // 雲端設定優先
  };
};

/**
 * 同步設定到 Firestore（節流處理，避免過於頻繁）
 */
let syncTimer: NodeJS.Timeout | null = null;
const SYNC_DELAY = 2000; // 2秒後同步

export const scheduleSyncToFirestore = (userId: string, settings: Partial<UserSettings>): void => {
  if (syncTimer) {
    clearTimeout(syncTimer);
  }
  
  syncTimer = setTimeout(async () => {
    try {
      await saveSettingsToFirestore(userId, settings);
      console.log('✅ 設定已同步到 Firestore');
    } catch (error) {
      console.error('❌ 同步設定到 Firestore 失敗:', error);
    }
  }, SYNC_DELAY);
};

/**
 * 初始化用戶設定（啟動時調用）
 */
export const initializeUserSettings = async (userId?: string): Promise<UserSettings> => {
  // 调试日志已移除
  
  // 1. 載入本地設定
  const localSettings = loadSettingsFromLocalStorage();
  
  // 2. 如果是登入用戶，載入雲端設定
  if (userId) {
    const cloudSettings = await loadSettingsFromFirestore(userId);
    
    // 3. 合併設定（雲端優先）
    const mergedSettings = mergeSettings(localSettings, cloudSettings);
    
    // 4. 保存到 localStorage（確保本地和雲端一致）
    saveSettingsToLocalStorage(mergedSettings);
    
    console.log('✅ 用戶設定初始化完成（已登入）:', mergedSettings);
    return mergedSettings;
  } else {
    // 游客用戶：只使用本地設定
    const mergedSettings = mergeSettings({}, localSettings);
    console.log('✅ 用戶設定初始化完成（游客）:', mergedSettings);
    return mergedSettings;
  }
};

/**
 * 更新單個設定項目
 */
export const updateUserSetting = async <K extends keyof UserSettings>(
  userId: string | undefined,
  key: K,
  value: UserSettings[K]
): Promise<void> => {
  // 1. 保存到 localStorage（立即生效）
  localStorage.setItem(key, String(value));
  console.log(`💾 已保存設定 ${key}:`, value);
  
  // 2. 如果是登入用戶，同步到 Firestore（節流處理）
  if (userId) {
    scheduleSyncToFirestore(userId, { [key]: value } as Partial<UserSettings>);
  }
};

